import React from "react";
import "./NinaSmartFlowPanel.css";

function NinaSmartFlowPanel({ analysis }) {
  const hasOrderFlowData =
    analysis.orderFlow.levelTwo.status === "Available" ||
    analysis.orderFlow.timeAndSales.status === "Available";
  const pulseItems = [
    {
      label: "Trend",
      value: analysis.trend.label,
      meta: analysis.trend.score === null
        ? "Unavailable"
        : `${analysis.trend.score}/100 · Derived`,
      tone: toneForDirection(analysis.trend.direction),
    },
    {
      label: "VWAP Position",
      value: analysis.vwap.position,
      meta: analysis.vwap.distancePercent === null
        ? "Unavailable"
        : `${Math.abs(analysis.vwap.distancePercent).toFixed(2)}% · Derived`,
      tone: toneForVwap(analysis.vwap.position),
    },
    {
      label: "Volume",
      value: analysis.volume.state,
      meta: analysis.volume.score === null
        ? "Unavailable"
        : `${analysis.volume.score}/100 · ${analysis.volume.provenance}`,
      tone: analysis.volume.state === "Confirmed"
        ? "positive"
        : analysis.volume.state === "Not confirmed"
        ? "danger"
        : "warning",
    },
    {
      label: "Pressure",
      value: analysis.pressure.side,
      meta: `${analysis.pressure.score}/100 estimated`,
      tone: analysis.pressure.side === "Buying pressure"
        ? "positive"
        : analysis.pressure.side === "Selling pressure"
        ? "danger"
        : "neutral",
    },
    {
      label: "Breakout Read",
      value: analysis.breakout.state,
      meta: "Derived price structure",
      tone: analysis.breakout.tone,
    },
  ];

  const planItems = [
    ["Suggested Entry Zone", analysis.plan.entryLabel, "entry"],
    ["Protective Stop", analysis.plan.stopLabel, "stop"],
    ["Target 1", analysis.plan.targetOneLabel, "target"],
    ["Target 2", analysis.plan.targetTwoLabel, "target"],
  ];

  return (
    <section
      className="ninaSmartFlowPanel"
      data-tour-id="nina-smartflow"
      aria-labelledby="nina-smartflow-heading"
    >
      <header className="ninaSmartFlowHeader">
        <div>
          <span>AI MARKET-STRUCTURE ANALYSIS</span>
          <h3 id="nina-smartflow-heading">AI SmartFlow™</h3>
          <p>
            {analysis.symbol} · {analysis.mode === "Simulated"
              ? "Simulated educational analysis"
              : "Authenticated evidence · derived analysis"}
          </p>
        </div>

        <div className="ninaSmartFlowHeaderStatus">
          <span>{analysis.mode} inputs</span>
          <strong>{analysis.disposition}</strong>
        </div>
      </header>

      <details className="ninaSmartFlowDetails">
        <summary>Open AI SmartFlow analysis</summary>
        <div className="ninaSmartFlowDetailsBody">
      <div className="ninaSmartFlowPulse">
        {pulseItems.map((item) => (
          <div
            className={`ninaSmartFlowPulseItem ${item.tone}`}
            key={item.label}
          >
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            <small>{item.meta}</small>
          </div>
        ))}
      </div>

      <div className="ninaSmartFlowPlan">
        {planItems.map(([label, value, tone]) => (
          <div className={tone} key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      <div className="ninaSmartFlowContext">
        <div className="ninaSmartFlowStructure">
          <span>Support / Resistance</span>
          <strong>
            {analysis.structure.supportLabel}
            <i aria-hidden="true">→</i>
            {analysis.structure.resistanceLabel}
          </strong>
        </div>

        <div>
          <span>Liquidity</span>
          <strong>{analysis.liquidity.value}</strong>
          <small>{analysis.liquidity.status}</small>
        </div>

        <div>
          <span>Institutional Activity</span>
          <strong>{analysis.institutionalActivity.value}</strong>
          <small>{analysis.institutionalActivity.status}</small>
        </div>

        <div>
          <span>Level II / Time &amp; Sales</span>
          <strong>
            {analysis.orderFlow.levelTwo.status} /{" "}
            {analysis.orderFlow.timeAndSales.status}
          </strong>
          <small>
            {hasOrderFlowData
              ? "Supplied order-flow data"
              : "No live order-flow feed connected"}
          </small>
        </div>
      </div>

      <div className="ninaSmartFlowExplanation">
        <span>Plain-Language AI Read</span>
        <p>{analysis.explanation}</p>
      </div>

      <p className="ninaSmartFlowDisclosure">
        Trend, pressure, and structure are derived analytics. Entry, stop, and
        targets are planning levels, not market-feed events. Liquidity,
        institutional activity, Level II, and Time &amp; Sales remain unavailable
        unless explicitly connected. SmartFlow does not place or route orders.
      </p>
        </div>
      </details>
    </section>
  );
}

function toneForDirection(direction) {
  if (direction === "Bullish") return "positive";
  if (direction === "Bearish") return "danger";
  return "neutral";
}

function toneForVwap(position) {
  if (position === "Above VWAP") return "positive";
  if (position === "Below VWAP") return "danger";
  return position === "At VWAP" ? "warning" : "neutral";
}

export default NinaSmartFlowPanel;
